import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

type MobileDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: 'left' | 'right';
  title?: string;
  /** Extra class on the panel (e.g. bg-sidebar-2) */
  className?: string;
  children: ReactNode;
};

/**
 * Slide-over panel for small screens. Uses Radix Dialog (focus trap, escape).
 * Intended for use below `md`; parent should only open when `useMediaQuery('(max-width: 767px)')` or always with `md:hidden` on overlay.
 */
export function MobileDrawer({
  open,
  onOpenChange,
  side = 'left',
  title,
  className,
  children,
}: MobileDrawerProps) {
  const { t } = useTranslation('common');
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-[100] bg-black/50 md:hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed top-0 bottom-0 z-[101] flex w-[min(360px,92vw)] max-w-[360px] flex-col bg-background shadow-modal outline-none md:hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            side === 'left'
              ? 'left-0 border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left'
              : 'right-0 border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            className
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-4">
            {title ? (
              <Dialog.Title className="text-lg font-semibold tracking-tight">{title}</Dialog.Title>
            ) : (
            <Dialog.Title className="sr-only">{t('shared.mobileDrawer.panelTitle')}</Dialog.Title>
            )}
            <Dialog.Close
              type="button"
              className="ml-auto rounded-xl p-2.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t('shared.mobileDrawer.close')}
            >
              <X size={22} />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
